import { ReportProfile } from './ReportProfile/ReportProfile'
import { MdVerified } from 'react-icons/md'
import { SocialButtons } from './SocialButtons'
import { MemberProfileExtendedType } from 'common/types/MemberProfile'
import { getMembershipExtendedColors } from '../../common/membershipStyles'
import { useEffect, useState } from 'react'

type ProfileHeaderLeftProps = {
  profile: MemberProfileExtendedType
}

const VerifiedBadge = () => {
  return (
    <div className='flex items-center'>
      <span>
        <MdVerified color='#20BFE7' />
      </span>
      <p className='text-xs text-[#001A3D] ml-1'>ID Verified</p>
    </div>
  )
}

export function ProfileHeaderLeft({ profile }: ProfileHeaderLeftProps) {
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    Utils.getWindowDimensions().width < 768,
  )
  const colors = getMembershipExtendedColors(profile?.metadata.membership_type)

  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const getInitials = (name: string) => {
    if (!name) {
      return 'NA'
    }
    if (profile.memberEntity === 'lawfirm') {
      return profile.core.practice_name_initials
    }
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
  }

  return (
    <>
      <div className='flex flex-row gap-2 md:gap-6 md:justify-center md:items-center h-[112px]'>
        <div className='relative'>
          {profile.core.avatar_url ? (
            <div
              style={{ backgroundImage: `url(${profile.core.avatar_url})` }}
              className='border-4 border-[#DDE3FB] w-12 h-12 md:w-24 md:h-24 rounded-full bg-contain bg-center bg-no-repeat'
              aria-label='profile photo'
            />
          ) : (
            <p
              className={`flex items-center justify-center border-4 border-[#DDE3FB] w-12 h-12 md:w-24 md:h-24 rounded-full ${colors.avatarBg}`}
            >
              {getInitials(profile.displayName)}
            </p>
          )}
          <svg
            className={`absolute -top-1 -left-[2px] w-14 h-14 md:top-[-12px] md:w-[108px] md:h-[120px] ${colors.avatarStrokeColor}`}
            viewBox='0 0 96 103'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M2.51492 26.6675C6.86679 19.5092 12.9963 13.5963 20.3091 9.50215C27.6219 5.40803 35.8699 3.27158 44.2535 3.29994C52.6371 3.32831 60.8719 5.52053 68.1593 9.66405C75.4468 13.8076 81.5397 19.7618 85.8474 26.9495C90.155 34.1372 92.5313 42.3145 92.7458 50.6885C92.9603 59.0625 91.0058 67.3492 87.0716 74.7451C83.1375 82.141 77.3573 88.3952 70.2914 92.9014C63.2255 97.4075 55.1137 100.013 46.7424 100.464'
              stroke='currentColor'
              stroke-width='6'
              stroke-linecap='round'
            />
          </svg>

          {isMobileOrTablet ? (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 5 140'
              fill='none'
              className={`absolute left-[22px] top-12 ${colors.avatarStrokeColor} h-[150px]`}
            >
              <path
                d='M3 140L3 2.99997'
                stroke='currentColor'
                strokeWidth='4'
                strokeLinecap='round'
              />
            </svg>
          ) : (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 8 120'
              fill='none'
              className={`absolute md:top-[100px] md:left-[45px] ${colors.avatarStrokeColor} h-[89px]`}
            >
              <path
                d='M3 120L3 2.99997'
                stroke='currentColor'
                strokeWidth='8'
                strokeLinecap='round'
              />
            </svg>
          )}
        </div>
        <div className='flex flex-col gap-1 ml-2'>
          <h1 className='max-w-30 sm:max-w-fit text-xs md:text-base font-bold no-wrap'>
            {profile.displayName}
          </h1>
          {profile.core.years_of_experience >= 1 && (
            <p className='text-xs text-[#6E7785]'>
              {profile.core.years_of_experience} years of experience
            </p>
          )}
          {profile.core.is_verified && <VerifiedBadge />}
          <SocialButtons
            email_address={profile.core.email_address}
            phone_number={profile.core.phone_number}
            linkedin_profile_url={profile.core.linkedin_profile_url}
          />
          <ReportProfile profile={profile} className='flex lg:hidden' />
        </div>
      </div>
    </>
  )
}
